const quotaWarningKeys = new Set<string>();

function isQuotaExceededError(err: unknown): err is { message?: string; name?: string; code?: number } {
  if (!err || typeof err !== 'object') {
    return false;
  }

  const quotaError = err as { message?: string; name?: string; code?: number };
  return (
    quotaError.name === 'QuotaExceededError'
    || quotaError.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || quotaError.code === 22
    || quotaError.code === 1014
    || /quota/i.test(quotaError.message ?? '')
  );
}

function stripApiData<T extends { api?: Record<string, unknown> }>(snapshot: T): T {
  if (!snapshot.api || typeof snapshot.api !== 'object') {
    return snapshot;
  }

  return {
    ...snapshot,
    api: {
      ...snapshot.api,
      data: null,
    },
  };
}

function buildPersistedState(state: any, mapUpdatedAt: string | null, discardHistory = false) {
  return {
    ...state,
    mapUpdatedAt,
    present: stripApiData(state.present),
    past: discardHistory ? [] : (state.past ?? []).map((snapshot: any) => stripApiData(snapshot)),
    future: discardHistory ? [] : (state.future ?? []).map((snapshot: any) => stripApiData(snapshot)),
  };
}

function persistSerializedState(state: any, id: string) {
  const serialState = JSON.stringify(state);
  localStorage.setItem(id, serialState);
}

export const loadState = (id: string) => {
  try {
    const serialState = localStorage.getItem(id);
    if (serialState === null) {
      return undefined;
    }
    return JSON.parse(serialState);
  } catch (_e) {
    return undefined;
  }
};

// mapUpdatedAt: server version of the map the draft is based on
export const saveState = (state: any, id: string, mapUpdatedAt: string | null = null) => {
  try {
    if (!state) {
      quotaWarningKeys.delete(id);
      localStorage.removeItem(id);
    } else {
      const attempts = [buildPersistedState(state, mapUpdatedAt)];
      if ((state.past?.length ?? 0) > 0 || (state.future?.length ?? 0) > 0) {
        attempts.push(buildPersistedState(state, mapUpdatedAt, true));
      }

      for (const candidate of attempts) {
        try {
          persistSerializedState(candidate, id);
          quotaWarningKeys.delete(id);
          return;
        } catch (err: any) {
          if (!isQuotaExceededError(err)) {
            console.error(err.message ?? err);
            return;
          }
        }
      }

      localStorage.removeItem(id);
      if (!quotaWarningKeys.has(id)) {
        quotaWarningKeys.add(id);
        console.warn('Draft autosave disabled for this map because browser storage quota was exceeded.');
      }
    }
  } catch (err: any) {
    console.error(err.message ?? err);
  }
};
